import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Collapse,
  IconButton,
  Card,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import { Search as SearchIcon, ExpandLess, ExpandMore, InfoOutlined as InfoIcon, KeyboardArrowDown, KeyboardArrowRight, OpenInNew } from '@mui/icons-material';
import { DataGrid, GridColDef, GridSortModel, GridColumnGroupingModel } from '@mui/x-data-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api/axios';
import { AcuityExpandPanel } from '../components/understaffing';

/* ───────────────── state names ───────────────── */
const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia', PR: 'Puerto Rico', GU: 'Guam', VI: 'Virgin Islands',
};

/* ───────────────── state legal/regulatory data ───────────────── */

// Staffing regime: states with numeric HPRD requirements
// null = no numeric minimum (non-numeric "sufficient staffing" standard)
// TODO: Populate with verified legal research — do not guess at state laws
const STAFFING_REGIME: Record<string, { hprd: number; note: string } | null> = {
  DC: { hprd: 4.1, note: 'D.C. Code § 44-1002.01 et seq.' },
  RI: { hprd: 3.81, note: 'R.I. Gen. Laws § 23-17.5-30' },
  DE: { hprd: 3.67, note: 'Del. Code Ann. tit. 16, § 1162; 16 Del. Admin. Code 3201' },
  FL: { hprd: 3.6, note: 'Fla. Stat. § 400.23' },
  MA: { hprd: 3.58, note: '105 C.M.R. 150.007' },
  CA: { hprd: 3.5, note: 'Cal. Health & Safety Code § 1276.65; Cal. Code Regs. tit. 22, § 72327' },
  NY: { hprd: 3.5, note: '10 N.Y.C.R.R. § 415.13; N.Y. Pub. Health Law Art. 28-D § 2895-b' },
  WA: { hprd: 3.4, note: 'Wash. Admin. Code § 388-97-0120' },
  PA: { hprd: 3.2, note: '28 Pa. Code § 211.12' },
  CT: { hprd: 3.0, note: 'Conn. Public Health Code § 19-13-D8t(m)' },
  MD: { hprd: 3.0, note: 'Md. Code Regs. 10.07.02.20' },
  VT: { hprd: 3.0, note: 'Vt. Stat. Ann. tit. 33, ch. 7' },
  OK: { hprd: 2.9, note: 'Okla. Admin. Code § 310:675-7-10' },
  MS: { hprd: 2.8, note: 'Miss. Admin. Code 15-301:41.29.3' },
  IL: { hprd: 2.5, note: '77 Ill. Admin. Code § 300.1230; 210 ILCS 45/2-211' },
  OH: { hprd: 2.5, note: 'Ohio Admin. Code § 3701-17-08' },
  ID: { hprd: 2.4, note: 'IDAPA 16.03.02.200' },
  LA: { hprd: 2.35, note: 'La. Admin. Code tit. 48, § 9783' },
  MI: { hprd: 2.25, note: 'Mich. Comp. Laws § 333.21720a(1)-(2)' },
  WV: { hprd: 2.25, note: 'W. Va. Code R. § 64-13-9' },
  CO: { hprd: 2.0, note: '6 Colo. Code Regs. 1011-1:7.8' },
  GA: { hprd: 2.0, note: 'Ga. Rules & Regs. Rule 111-8-56-.04' },
  MN: { hprd: 2.0, note: 'Minn. Stat. § 144A.04; Minn. R. 4658.0515' },
  TN: { hprd: 2.0, note: 'Tenn. Comp. R. & Regs. 1200-08-06-.06' },
  WI: { hprd: 2.0, note: 'Wis. Admin. Code DHS § 132.62' },
  KS: { hprd: 1.85, note: 'Kan. Admin. Regs. § 28-39-145a' },
  WY: { hprd: 1.5, note: 'Wyo. Admin. Regs. Ch. 4, § 7' },
  // States with no numeric HPRD minimum (shift-based or non-numeric):
  NJ: null,  // Shift-based ratios, N.J. Admin. Code tit. 8, ch. 39
  // No entry = no numeric minimum (TX, IN, MO, NC, IA, VA, KY, AL, AK, AR, AZ, HI, NE, NV, NM, MT, ND, SD, SC, UT, OR, ME, NH)
};

// Qui tam access: does state have a False Claims Act with qui tam provision?
// TODO: Populate with verified legal research — do not guess at state laws
const QUI_TAM_ACCESS: Record<string, { access: boolean; note: string }> = {
  AL: { access: false, note: 'No state FCA' },
  AK: { access: false, note: 'No state FCA' },
  AZ: { access: false, note: 'No state FCA' },
  AR: { access: false, note: 'Informant reward of up to 10% recovery' },
  CA: { access: true, note: 'State FCA with qui tam provision' },
  CO: { access: true, note: 'State FCA with qui tam provision' },
  CT: { access: true, note: 'State FCA with qui tam provision' },
  DE: { access: true, note: 'State FCA with qui tam provision' },
  FL: { access: true, note: 'State FCA with qui tam provision' },
  GA: { access: true, note: 'State FCA with qui tam provision' },
  HI: { access: true, note: 'State FCA with qui tam provision' },
  ID: { access: false, note: 'No state FCA' },
  IL: { access: true, note: 'State FCA with qui tam provision' },
  IN: { access: true, note: 'State FCA with qui tam provision' },
  IA: { access: true, note: 'State FCA with qui tam provision' },
  KS: { access: false, note: 'No state FCA' },
  KY: { access: false, note: 'No state FCA' },
  LA: { access: true, note: 'Medicaid only' },
  ME: { access: true, note: 'State FCA with qui tam provision' },
  MD: { access: true, note: 'State FCA with qui tam provision' },
  MA: { access: true, note: 'State FCA with qui tam provision' },
  MI: { access: true, note: 'Medicaid only' },
  MN: { access: true, note: 'State FCA with qui tam provision' },
  MS: { access: false, note: 'No state FCA' },
  MO: { access: false, note: 'Informant reward of up to 10% recovery' },
  MT: { access: true, note: 'State FCA with qui tam provision' },
  NE: { access: false, note: 'No state FCA' },
  NV: { access: true, note: 'State FCA with qui tam provision' },
  NH: { access: true, note: 'Medicaid only' },
  NJ: { access: true, note: 'State FCA with qui tam provision' },
  NM: { access: true, note: 'State FCA with qui tam provision' },
  NY: { access: true, note: 'State FCA with qui tam provision' },
  NC: { access: true, note: 'State FCA with qui tam provision' },
  ND: { access: false, note: 'No state FCA' },
  OH: { access: false, note: 'No state FCA' },
  OK: { access: true, note: 'Medicaid only' },
  OR: { access: false, note: 'No state FCA' },
  PA: { access: false, note: 'No state FCA' },
  RI: { access: true, note: 'State FCA with qui tam provision' },
  SC: { access: false, note: 'No state FCA' },
  SD: { access: false, note: 'No state FCA' },
  TN: { access: true, note: 'State FCA with qui tam provision' },
  TX: { access: true, note: 'Medicaid only' },
  UT: { access: false, note: 'No state FCA' },
  VT: { access: true, note: 'State FCA with qui tam provision' },
  VA: { access: true, note: 'State FCA with qui tam provision' },
  WA: { access: true, note: 'Medicaid only' },
  WV: { access: false, note: 'No state FCA' },
  WI: { access: false, note: 'No state FCA' },
  WY: { access: false, note: 'No state FCA' },
};

/* ───────────────── helpers ───────────────── */

const formatMoney = (val: number | null | undefined): string => {
  if (val == null) return '-';
  const prefix = val < 0 ? '-$' : '$';
  const abs = Math.abs(val);
  if (abs >= 1e9) return `${prefix}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${prefix}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${prefix}${(abs / 1e3).toFixed(0)}K`;
  return `${prefix}${abs.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatNumber = (val: number | null | undefined, decimals = 0): string => {
  if (val == null) return '-';
  return val.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatPercent = (val: number | null | undefined): string => {
  if (val == null) return '-';
  return `${val.toFixed(1)}%`;
};

const formatHours = (val: number | null | undefined): string => {
  if (val == null) return '-';
  const abs = Math.abs(val);
  if (abs >= 1e6) return `${(abs / 1e6).toFixed(1)}M hrs`;
  if (abs >= 1e3) return `${(abs / 1e3).toFixed(1)}K hrs`;
  return `${abs.toFixed(0)} hrs`;
};

/* ───────────────── styles (matching state page) ───────────────── */

const dataGridStyles = {
  backgroundColor: 'white',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden',
  height: '100%',
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
    backgroundColor: 'action.hover',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    paddingTop: 0,
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    padding: '12px 8px',
    fontSize: '0.9375rem',
  },
  '& .MuiDataGrid-cell:first-of-type': {
    paddingLeft: '24px',
  },
  '& .MuiDataGrid-cell:last-of-type': {
    paddingRight: '24px',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#e0e0e0',
    borderBottom: '3px solid #9e9e9e',
    maxHeight: 'none !important',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: '#e0e0e0',
    padding: '8px 8px',
    height: 'auto !important',
    maxHeight: 'none !important',
    alignItems: 'center',
  },
  '& .MuiDataGrid-columnHeader:first-of-type': {
    paddingLeft: '24px',
  },
  '& .MuiDataGrid-columnHeader:last-of-type': {
    paddingRight: '24px',
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    overflow: 'visible',
    whiteSpace: 'normal',
    lineHeight: 1.3,
  },
  '& .MuiDataGrid-columnHeaderTitleContainerContent': {
    overflow: 'visible',
    whiteSpace: 'normal',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: 'text.primary',
    fontWeight: 700,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1.3,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflow: 'visible',
    textOverflow: 'unset',
  },
  '& .MuiDataGrid-iconButtonContainer': {
    display: 'none',
  },
  '& .MuiDataGrid-sortIcon': {
    display: 'none',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderTopColor: 'divider',
    backgroundColor: '#fafafa',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  // Detail panel rows (expanded acuity chart)
  '& .detail-panel-row': {
    backgroundColor: '#fafbfc',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.04)',
    cursor: 'default !important',
  },
  '& .detail-panel-row:hover': {
    backgroundColor: '#fafbfc !important',
    cursor: 'default !important',
  },
  '& .detail-panel-row .MuiDataGrid-cell': {
    padding: '16px 24px !important',
    borderBottom: '2px solid',
    borderBottomColor: 'divider',
  },
  // Section dividers
  '& .section-divider-cell': {
    borderLeft: '1px solid',
    borderLeftColor: 'divider',
  },
  '& .MuiDataGrid-columnHeader.section-divider-header': {
    borderLeft: '1px solid',
    borderLeftColor: 'divider',
  },
  // Column group header row
  '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer': {
    justifyContent: 'center',
  },
  '& .MuiDataGrid-columnHeader--filledGroup': {
    paddingTop: '10px !important',
    paddingBottom: '0px !important',
    alignItems: 'flex-end !important',
  },
  '& .MuiDataGrid-columnHeader.group-divider-left': {
    borderLeft: '1px solid',
    borderLeftColor: 'divider',
  },
  // Remove MUI default right borders on group headers
  '& .MuiDataGrid-columnHeader--withRightBorder': {
    borderRight: 'none !important',
  },
  '& .MuiDataGrid-cell--withRightBorder': {
    borderRight: 'none !important',
  },
};

/* ───────────────── types ───────────────── */

interface StateRow {
  state: string;
  num_facilities: number;
  total_ant_cost: number | null;
  hybrid_mds_cost: number | null;
  avg_acuity_gap: number | null;
  total_rp_paid: number | null;
  total_harm_citations: number | null;
  avg_hprd: number | null;
  total_understaffed_hprd_hrs: number | null;
}

interface OwnerGroupRow {
  group_id: string;
  primary_owner_name: string;
  primary_type_owner: string;
  num_states: number;
  num_facilities: number;
  total_ant_cost: number | null;
  total_ant_cost_no_credit: number | null;
  hybrid_mds_cost: number | null;
  total_rp_paid: number | null;
  avg_acuity_gap: number | null;
  avg_acuity_rn: number | null;
  avg_acuity_lpn: number | null;
  avg_acuity_aide: number | null;
  pct_ant_days_any_below: number | null;
  pct_days_below_cms_hprd: number | null;
  state_list: string[];
}

/* ───────────────── component ───────────────── */

export default function NationalLandscape() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Scheme ID
  const [schemeId, setSchemeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Header expand/collapse
  const [headerExpanded, setHeaderExpanded] = useState(false);

  // Toggle: 'states' or 'ownerGroups'
  const [activeView, setActiveView] = useState<'states' | 'ownerGroups'>(
    searchParams.get('view') === 'ownerGroups' ? 'ownerGroups' : 'states'
  );

  // State rankings (client-side sort/pagination, ~53 rows)
  const [stateRows, setStateRows] = useState<StateRow[]>([]);
  const [nationalSummary, setNationalSummary] = useState<Record<string, any> | null>(null);
  const [statesLoading, setStatesLoading] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const [stateSortModel, setStateSortModel] = useState<GridSortModel>([{ field: 'state', sort: 'asc' }]);

  // Owner groups (server-side sort/pagination)
  const [ownerGroups, setOwnerGroups] = useState<OwnerGroupRow[]>([]);
  const [ownerGroupsLoading, setOwnerGroupsLoading] = useState(false);
  const [ownerGroupsRowCount, setOwnerGroupsRowCount] = useState(0);
  const [ownerGroupsPagination, setOwnerGroupsPagination] = useState({ page: 0, pageSize: 25 });
  const [ownerGroupsSortModel, setOwnerGroupsSortModel] = useState<GridSortModel>([{ field: 'hybrid_mds_cost', sort: 'desc' }]);
  const [ownerGroupsSearch, setOwnerGroupsSearch] = useState('');
  const [ownerGroupsStateFilter, setOwnerGroupsStateFilter] = useState<string>('');

  // Expandable rows for owner groups
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleRowExpand = useCallback((rowId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  // Fetch scheme ID on mount
  useEffect(() => {
    const fetchScheme = async () => {
      try {
        const res = await api.get('/schemes/');
        const schemes = res.data.results ?? res.data;
        const understaffingScheme = schemes.find((s: any) => s.code === 'snf-understaffing');
        if (understaffingScheme) {
          setSchemeId(understaffingScheme.id);
        }
      } catch (err) {
        console.error('Failed to fetch schemes', err);
      } finally {
        setLoading(false);
      }
    };
    fetchScheme();
  }, []);

  // Fetch national summary (instant — pre-computed single row)
  useEffect(() => {
    if (!schemeId) return;
    api.get(`/schemes/${schemeId}/understaffing/national-summary/`)
      .then(res => setNationalSummary(res.data || null))
      .catch(err => console.error('Failed to fetch national summary', err));
  }, [schemeId]);

  // Fetch state rankings (heavier query — loads independently)
  useEffect(() => {
    if (!schemeId) return;
    const fetchStateRankings = async () => {
      setStatesLoading(true);
      try {
        const res = await api.get(`/schemes/${schemeId}/understaffing/state-rankings/`);
        setStateRows(res.data.states || []);
      } catch (err) {
        console.error('Failed to fetch state rankings', err);
      } finally {
        setStatesLoading(false);
      }
    };
    fetchStateRankings();
  }, [schemeId]);

  // Fetch owner groups (server-side)
  const fetchOwnerGroups = useCallback(async () => {
    if (!schemeId) return;
    setOwnerGroupsLoading(true);
    try {
      const params: any = {
        page: ownerGroupsPagination.page + 1,
        page_size: ownerGroupsPagination.pageSize,
      };
      if (ownerGroupsSortModel.length > 0) {
        params.sort = ownerGroupsSortModel[0].field;
        params.order = ownerGroupsSortModel[0].sort || 'desc';
      }
      if (ownerGroupsSearch) params.search = ownerGroupsSearch;

      // Use state-specific endpoint when filtering by state, national endpoint otherwise
      let endpoint = `/schemes/${schemeId}/understaffing/owner-groups/`;
      if (ownerGroupsStateFilter) {
        endpoint = `/schemes/${schemeId}/understaffing/owner-groups-state/`;
        params.state = ownerGroupsStateFilter;
      }

      const res = await api.get(endpoint, { params });
      const results = res.data.results || [];
      // Inject state_list when using state-specific endpoint (it doesn't return state_list)
      if (ownerGroupsStateFilter) {
        results.forEach((r: any) => { if (!r.state_list) r.state_list = [ownerGroupsStateFilter.toUpperCase()]; });
      }
      setOwnerGroups(results);
      setOwnerGroupsRowCount(res.data.total_count || 0);
    } catch (err) {
      console.error('Failed to fetch owner groups', err);
    } finally {
      setOwnerGroupsLoading(false);
    }
  }, [schemeId, ownerGroupsPagination, ownerGroupsSortModel, ownerGroupsSearch, ownerGroupsStateFilter]);

  useEffect(() => {
    if (activeView === 'ownerGroups') {
      fetchOwnerGroups();
    }
  }, [fetchOwnerGroups, activeView]);

  // State acuity averages lookup (for owner group expand panels)
  const stateAcuityMap = useMemo(() => {
    const map: Record<string, { rn: number | null; lpn: number | null; aide: number | null; total: number | null }> = {};
    for (const row of stateRows) {
      map[row.state] = {
        rn: (row as any).avg_acuity_rn ?? null,
        lpn: (row as any).avg_acuity_lpn ?? null,
        aide: (row as any).avg_acuity_aide ?? null,
        total: (row as any).avg_acuity_gap ?? null,
      };
    }
    return map;
  }, [stateRows]);

  // State hybrid acuity averages lookup (for owner group expand panels)
  const stateHybridAcuityMap = useMemo(() => {
    const map: Record<string, { rn: number | null; lpn: number | null; aide: number | null; total: number | null }> = {};
    for (const row of stateRows) {
      map[row.state] = {
        rn: (row as any).hybrid_acuity_rn ?? null,
        lpn: (row as any).hybrid_acuity_lpn ?? null,
        aide: (row as any).hybrid_acuity_aide ?? null,
        total: (row as any).hybrid_acuity_total ?? null,
      };
    }
    return map;
  }, [stateRows]);

  // Expandable rows for states
  const [expandedStateRows, setExpandedStateRows] = useState<Set<string>>(new Set());
  const toggleStateRowExpand = useCallback((state: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedStateRows(prev => {
      const next = new Set(prev);
      if (next.has(state)) next.delete(state);
      else next.add(state);
      return next;
    });
  }, []);

  // National acuity averages (for state expand panel comparison)
  const nationalAcuity = useMemo(() => ({
    rn: (nationalSummary as any)?.avg_acuity_rn ?? null,
    lpn: (nationalSummary as any)?.avg_acuity_lpn ?? null,
    aide: (nationalSummary as any)?.avg_acuity_aide ?? null,
    total: (nationalSummary as any)?.avg_acuity_gap ?? null,
  }), [nationalSummary]);

  const nationalAcuityHybrid = useMemo(() => ({
    rn: (nationalSummary as any)?.hybrid_acuity_rn ?? null,
    lpn: (nationalSummary as any)?.hybrid_acuity_lpn ?? null,
    aide: (nationalSummary as any)?.hybrid_acuity_aide ?? null,
    total: (nationalSummary as any)?.hybrid_acuity_total ?? null,
  }), [nationalSummary]);

  // Filtered state rows (client-side search)
  const filteredStateRows = React.useMemo(() => {
    if (!stateSearch) return stateRows;
    const q = stateSearch.toLowerCase();
    return stateRows.filter((r) => {
      const name = (STATE_NAMES[r.state] || '').toLowerCase();
      return r.state.toLowerCase().includes(q) || name.includes(q);
    });
  }, [stateRows, stateSearch]);

  // Processed state rows: sort, then insert detail rows after expanded rows
  const processedStateRows = useMemo(() => {
    // Sort filtered rows client-side
    let sorted = [...filteredStateRows];
    if (stateSortModel.length > 0) {
      const { field, sort } = stateSortModel[0];
      const dir = sort === 'desc' ? -1 : 1;
      sorted.sort((a: any, b: any) => {
        let va = a[field] ?? '';
        let vb = b[field] ?? '';
        if (field === 'state') { va = STATE_NAMES[va] || va; vb = STATE_NAMES[vb] || vb; }
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
    }
    // Insert detail rows
    const result: any[] = [];
    for (const row of sorted) {
      result.push(row);
      if (expandedStateRows.has(row.state)) {
        result.push({
          _isDetailRow: true,
          _parentRow: row,
          state: `detail-${row.state}`,
        });
      }
    }
    return result;
  }, [filteredStateRows, expandedStateRows, stateSortModel]);

  // Processed owner group rows: insert detail rows after expanded rows
  const processedOwnerGroupRows = useMemo(() => {
    const result: any[] = [];
    for (const row of ownerGroups) {
      result.push(row);
      if (expandedRows.has(row.group_id)) {
        result.push({
          _isDetailRow: true,
          _parentRow: row,
          group_id: `detail-${row.group_id}`,
        });
      }
    }
    return result;
  }, [ownerGroups, expandedRows]);

  // ---- Column Definitions ----

  // Info icon helper for tooltip columns
  const InfoTooltip = ({ title }: { title: string }) => (
    <Tooltip title={title} arrow>
      <InfoIcon sx={{ fontSize: 13, color: 'text.disabled', ml: 0.5, cursor: 'help' }} />
    </Tooltip>
  );

  const stateColumns: GridColDef[] = [
    // ── Expand/collapse ──
    {
      field: '__expand',
      headerName: '',
      width: 36,
      sortable: false,
      disableColumnMenu: true,
      colSpan: (_value: any, row: any) => row._isDetailRow ? 99 : undefined,
      renderCell: ({ row }: any) => {
        if (row._isDetailRow) {
          const parentRow = row._parentRow;
          const acuity = {
            rn: parentRow.avg_acuity_rn ?? null,
            lpn: parentRow.avg_acuity_lpn ?? null,
            aide: parentRow.avg_acuity_aide ?? null,
            total: parentRow.avg_acuity_gap ?? null,
          };
          const acuityHybrid = {
            rn: parentRow.hybrid_acuity_rn ?? null,
            lpn: parentRow.hybrid_acuity_lpn ?? null,
            aide: parentRow.hybrid_acuity_aide ?? null,
            total: parentRow.hybrid_acuity_total ?? null,
          };
          const daysBelow = {
            rn: parentRow.pct_days_below_acuity_rn ?? null,
            lpn: parentRow.pct_days_below_acuity_lpn ?? null,
            cna: parentRow.pct_days_below_acuity_cna ?? null,
          };
          const daysBelowHybrid = {
            rn: parentRow.hybrid_pct_days_below_acuity_rn ?? null,
            lpn: parentRow.hybrid_pct_days_below_acuity_lpn ?? null,
            cna: parentRow.hybrid_pct_days_below_acuity_cna ?? null,
          };
          return (
            <Box sx={{ width: '100%' }}>
              <AcuityExpandPanel
                facilityAcuity={acuity}
                facilityAcuityHybrid={acuityHybrid}
                stateAcuity={nationalAcuity}
                stateAcuityHybrid={nationalAcuityHybrid}
                pctDaysBelowAcuity={daysBelow}
                pctDaysBelowAcuityHybrid={daysBelowHybrid}
                primaryLabel="State"
                comparisonLabel="National"
              />
            </Box>
          );
        }
        const isExpanded = expandedStateRows.has(row.state);
        return (
          <IconButton size="small" onClick={(e: React.MouseEvent) => toggleStateRowExpand(row.state, e)} sx={{ p: 0.5 }}>
            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </IconButton>
        );
      },
    },
    // ── Identity ──
    {
      field: 'state',
      headerName: 'State',
      flex: 0.6,
      minWidth: 110,
      renderCell: (params) => {
        if (params.row._isDetailRow) return null;
        return (
          <Typography fontWeight={700} fontSize="0.875rem">
            {STATE_NAMES[params.value] || params.value}
          </Typography>
        );
      },
    },
    {
      field: 'num_facilities',
      headerName: '#',
      flex: 0.3,
      minWidth: 50,
      type: 'number',
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      renderCell: (params) => (
        <Typography fontSize="0.875rem">{formatNumber(params.value)}</Typography>
      ),
    },
    /* ── Anticipated Model columns hidden (national tables show Hybrid only) ──
    {
      field: 'total_ant_cost_no_credit',
      headerName: 'Projected Total Understaffing — No Credit',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      headerClassName: 'section-divider-header',
      cellClassName: 'section-divider-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Projected Total Understaffing</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>No Credit</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Sum Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const v = Number(params.value) || 0;
        return v ? <Typography variant="body2" fontWeight={700}>{formatMoney(v)}</Typography> : <Typography variant="body2">-</Typography>;
      },
    },
    {
      field: 'avg_acuity_gap',
      headerName: 'Total Acuity Gap',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Total Acuity Gap</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return <Typography variant="body2">-</Typography>;
        const deficit = Number(val) - 100;
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: deficit < -25 ? 'error.main' : deficit < -10 ? 'warning.dark' : 'text.primary' }}>
            {deficit.toFixed(1)}%
          </Typography>
        );
      },
    },
    {
      field: 'pct_ant_days_any_below',
      headerName: '% Days Failing to Staff to Acuity',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>% Days Failing to Staff</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>to Acuity</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Total Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return <Typography variant="body2">-</Typography>;
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: Number(val) > 50 ? 'error.main' : Number(val) > 25 ? 'warning.dark' : 'text.primary' }}>
            {Number(val).toFixed(1)}%
          </Typography>
        );
      },
    },
    */
    // ── Hybrid Model ──
    {
      field: 'hybrid_mds_cost',
      headerName: 'Projected Total Understaffing — With Credit (Hybrid)',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      headerClassName: 'section-divider-header',
      cellClassName: 'section-divider-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Projected Total Understaffing</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>With Credit</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Sum Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const v = Math.max(0, Number(params.value) || 0);
        return v ? <Typography variant="body2" fontWeight={700}>{formatMoney(v)}</Typography> : <Typography variant="body2">$0</Typography>;
      },
    },
    {
      field: 'hybrid_pct_days_any_below',
      headerName: '% Days Failing to Staff to Acuity',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>% Days Failing to Staff</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>to Acuity</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Total Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return <Typography variant="body2">-</Typography>;
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: Number(val) > 50 ? 'error.main' : Number(val) > 25 ? 'warning.dark' : 'text.primary' }}>
            {Number(val).toFixed(1)}%
          </Typography>
        );
      },
    },
    // ── State Minimum ──
    {
      field: 'pct_days_below_cms_hprd',
      headerName: '% Days Staffed to State Minimum',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      headerClassName: 'section-divider-header',
      cellClassName: 'section-divider-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>% Days Staffed to</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>State Minimum</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Total Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const pctBelow = params.value;
        if (pctBelow == null) return <Typography variant="body2">-</Typography>;
        const pctStaffed = 100 - Number(pctBelow);
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: pctStaffed < 50 ? 'error.main' : pctStaffed < 75 ? 'warning.dark' : 'text.primary' }}>
            {pctStaffed.toFixed(1)}%
          </Typography>
        );
      },
    },
    // ── Legal Landscape ──
    {
      field: 'qui_tam',
      headerName: 'Qui Tam',
      flex: 0.5,
      minWidth: 70,
      sortable: false,
      align: 'center' as const,
      headerAlign: 'center' as const,
      headerClassName: 'section-divider-header',
      cellClassName: 'section-divider-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Qui Tam</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>State FCA</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const qt = QUI_TAM_ACCESS[params.row.state];
        if (!qt) return <Typography fontSize="0.85rem" color="text.disabled">—</Typography>;
        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            <Typography fontSize="0.85rem" fontWeight={600} color={qt.access ? 'success.main' : 'text.disabled'}>
              {qt.access ? 'Yes' : 'No'}
            </Typography>
            {qt.note !== 'No state FCA' && qt.note !== 'State FCA with qui tam provision'
              ? <InfoTooltip title={qt.note} />
              : <Box sx={{ width: 16 }} />}
          </Box>
        );
      },
    },
    {
      field: 'staffing_regime',
      headerName: 'Staffing Regime',
      flex: 0.6,
      minWidth: 90,
      sortable: false,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Staffing Regime</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Numeric HPRD</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const regime = STAFFING_REGIME[params.row.state];
        if (!regime) return <Typography fontSize="0.85rem" color="text.disabled">—</Typography>;
        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography fontSize="0.85rem" fontWeight={600}>{regime.hprd.toFixed(2)}</Typography>
            <Typography fontSize="0.75rem" color="text.disabled">HPRD</Typography>
          </Box>
        );
      },
    },
    // ── Navigate ──
    {
      field: '__navigate',
      headerName: '',
      width: 36,
      sortable: false,
      disableColumnMenu: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      headerClassName: 'section-divider-header',
      cellClassName: 'section-divider-cell',
      renderCell: ({ row }: any) => {
        if (row._isDetailRow) return null;
        return (
          <IconButton
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              navigate(`/schemes/snf-understaffing/state/${row.state}`);
            }}
            sx={{ color: 'text.secondary', p: 0.25 }}
          >
            <OpenInNew sx={{ fontSize: 14 }} />
          </IconButton>
        );
      },
    },
  ];

  const stateColumnGroups: GridColumnGroupingModel = (() => {
    const renderGroup = (label: string, color: string) => () => (
      <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', color }}>
        {label}
      </Typography>
    );
    return [
      // { groupId: 'anticipated_model', headerName: 'Anticipated Model', headerClassName: 'group-divider-left', children: [{ field: 'total_ant_cost_no_credit' }, { field: 'avg_acuity_gap' }, { field: 'pct_ant_days_any_below' }], renderHeaderGroup: renderGroup('Anticipated Model', 'rgba(21, 101, 192, 0.85)') },
      { groupId: 'hybrid_model', headerName: 'Hybrid Model', headerClassName: 'group-divider-left', children: [{ field: 'hybrid_mds_cost' }, { field: 'hybrid_pct_days_any_below' }], renderHeaderGroup: renderGroup('Hybrid Model', 'rgba(123, 31, 162, 0.85)') },
      { groupId: 'state_minimum', headerName: 'State Minimum', headerClassName: 'group-divider-left', children: [{ field: 'pct_days_below_cms_hprd' }], renderHeaderGroup: renderGroup('State Minimum', 'rgba(46, 125, 50, 0.85)') },
      { groupId: 'legal_landscape', headerName: 'Legal Landscape', headerClassName: 'group-divider-left', children: [{ field: 'qui_tam' }, { field: 'staffing_regime' }], renderHeaderGroup: renderGroup('Legal Landscape', 'rgba(230, 81, 0, 0.85)') },
      { groupId: 'nav_group', headerName: '', headerClassName: 'group-divider-left', children: [{ field: '__navigate' }], renderHeaderGroup: () => null },
    ];
  })();

  const ownerGroupColumns: GridColDef[] = [
    // ── Expand/collapse ──
    {
      field: '__expand',
      headerName: '',
      width: 40,
      sortable: false,
      disableColumnMenu: true,
      colSpan: (_value: any, row: any) => row._isDetailRow ? 99 : undefined,
      renderCell: ({ row }: any) => {
        if (row._isDetailRow) {
          // Detail row: render the AcuityExpandPanel
          const parentRow = row._parentRow;
          const acuity = {
            rn: parentRow.avg_acuity_rn ?? null,
            lpn: parentRow.avg_acuity_lpn ?? null,
            aide: parentRow.avg_acuity_aide ?? null,
            total: parentRow.avg_acuity_gap ?? null,
          };
          const acuityHybrid = {
            rn: parentRow.hybrid_acuity_rn ?? null,
            lpn: parentRow.hybrid_acuity_lpn ?? null,
            aide: parentRow.hybrid_acuity_aide ?? null,
            total: parentRow.hybrid_acuity_total ?? null,
          };
          const daysBelow = {
            rn: parentRow.pct_ant_days_below_rn ?? null,
            lpn: parentRow.pct_ant_days_below_lpn ?? null,
            cna: parentRow.pct_ant_days_below_cna ?? null,
          };
          const daysBelowHybrid = {
            rn: parentRow.hybrid_pct_days_below_rn ?? null,
            lpn: parentRow.hybrid_pct_days_below_lpn ?? null,
            cna: parentRow.hybrid_pct_days_below_cna ?? null,
          };
          const groupState = parentRow.state_list?.[0] || '';
          const stateAvg = stateAcuityMap[groupState] || { rn: null, lpn: null, aide: null, total: null };
          const stateAvgHybrid = stateHybridAcuityMap[groupState] || { rn: null, lpn: null, aide: null, total: null };
          return (
            <Box sx={{ width: '100%' }}>
              <AcuityExpandPanel
                facilityAcuity={acuity}
                facilityAcuityHybrid={acuityHybrid}
                stateAcuity={stateAvg}
                stateAcuityHybrid={stateAvgHybrid}
                pctDaysBelowAcuity={daysBelow}
                pctDaysBelowAcuityHybrid={daysBelowHybrid}
              />
            </Box>
          );
        }
        const rowId = row.group_id;
        const isExpanded = expandedRows.has(rowId);
        return (
          <IconButton size="small" onClick={(e: React.MouseEvent) => toggleRowExpand(rowId, e)} sx={{ p: 0.5 }}>
            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </IconButton>
        );
      },
    },
    // ── Identity ──
    {
      field: 'primary_owner_name',
      headerName: 'Group',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }: any) => {
        const coOwners: string[] = (row.co_owner_names || []).filter(
          (name: string) => name !== row.primary_owner_name
        );
        const allNames = [row.primary_owner_name, ...coOwners];
        const displayNames = allNames.slice(0, 5);
        const remainingCount = allNames.length - 5;
        return (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 1.3, py: 0.5 }}>
            {displayNames.map((name: string, i: number) => (
              <Typography key={i} variant="body2" fontWeight={i === 0 ? 600 : 500}
                sx={{ wordBreak: 'break-word', color: i === 0 ? 'text.primary' : 'text.secondary', fontSize: i === 0 ? undefined : '0.8rem' }}>
                {name}
              </Typography>
            ))}
            {remainingCount > 0 && (
              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.75rem', fontStyle: 'italic' }}>
                +{remainingCount} more
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'state_list',
      headerName: 'State',
      width: 65,
      sortable: false,
      renderCell: (params) => {
        const states: string[] = params.value || [];
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {states.map((s: string) => (
              <Chip key={s} label={s} size="small" variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600 }} />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'num_facilities',
      headerName: '#',
      width: 55,
      type: 'number',
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      renderCell: (params) => <Typography fontSize="0.875rem">{formatNumber(params.value)}</Typography>,
    },
    /* ── Anticipated Model columns hidden (national tables show Hybrid only) ──
    {
      field: 'total_ant_cost_no_credit',
      headerName: 'Projected Total Understaffing — No Credit',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      headerClassName: 'section-divider-header',
      cellClassName: 'section-divider-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Projected Total Understaffing</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>No Credit</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Sum Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const v = Number(params.value) || 0;
        return v ? <Typography variant="body2" fontWeight={700}>{formatMoney(v)}</Typography> : <Typography variant="body2">-</Typography>;
      },
    },
    {
      field: 'avg_acuity_gap',
      headerName: 'Total Acuity Gap',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Total Acuity Gap</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return <Typography variant="body2">-</Typography>;
        const deficit = Number(val) - 100;
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: deficit < -25 ? 'error.main' : deficit < -10 ? 'warning.dark' : 'text.primary' }}>
            {deficit.toFixed(1)}%
          </Typography>
        );
      },
    },
    {
      field: 'pct_ant_days_any_below',
      headerName: '% Days Failing to Staff to Acuity',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>% Days Failing to Staff</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>to Acuity</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Total Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return <Typography variant="body2">-</Typography>;
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: Number(val) > 50 ? 'error.main' : Number(val) > 25 ? 'warning.dark' : 'text.primary' }}>
            {Number(val).toFixed(1)}%
          </Typography>
        );
      },
    },
    */
    // ── Hybrid Model ──
    {
      field: 'hybrid_mds_cost',
      headerName: 'Projected Total Understaffing — With Credit (Hybrid)',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      headerClassName: 'section-divider-header',
      cellClassName: 'section-divider-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Projected Total Understaffing</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>With Credit</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Sum Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const v = Math.max(0, Number(params.value) || 0);
        return v ? <Typography variant="body2" fontWeight={700}>{formatMoney(v)}</Typography> : <Typography variant="body2">$0</Typography>;
      },
    },
    {
      field: 'hybrid_pct_days_any_below',
      headerName: '% Days Failing to Staff to Acuity',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>% Days Failing to Staff</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>to Acuity</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Total Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return <Typography variant="body2">-</Typography>;
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: Number(val) > 50 ? 'error.main' : Number(val) > 25 ? 'warning.dark' : 'text.primary' }}>
            {Number(val).toFixed(1)}%
          </Typography>
        );
      },
    },
    // ── State Minimum ──
    {
      field: 'pct_days_below_cms_hprd',
      headerName: '% Days Staffed to State Minimum',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      sortingOrder: ['desc', 'asc'] as ('desc' | 'asc')[],
      headerClassName: 'section-divider-header',
      cellClassName: 'section-divider-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>% Days Staffed to</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>State Minimum</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Total Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020-2025</Typography>
        </Box>
      ),
      renderCell: (params) => {
        const pctBelow = params.value;
        if (pctBelow == null) return <Typography variant="body2">-</Typography>;
        const pctStaffed = 100 - Number(pctBelow);
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: pctStaffed < 50 ? 'error.main' : pctStaffed < 75 ? 'warning.dark' : 'text.primary' }}>
            {pctStaffed.toFixed(1)}%
          </Typography>
        );
      },
    },
    // ── Navigate ──
    {
      field: '__navigate',
      headerName: '',
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: 'section-divider-header',
      cellClassName: 'section-divider-cell',
      renderCell: ({ row }: any) => {
        if (row._isDetailRow) return null;
        const state = row.state_list?.[0] || '';
        return (
          <IconButton
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              navigate(`/schemes/snf-understaffing/owner-group/${encodeURIComponent(row.group_id)}?from=national${state ? `&state=${state}` : ''}`);
            }}
            sx={{ color: 'text.secondary' }}
          >
            <OpenInNew sx={{ fontSize: 16 }} />
          </IconButton>
        );
      },
    },
  ];

  const ownerGroupColumnGroups: GridColumnGroupingModel = (() => {
    const renderGroup = (label: string, color: string) => () => (
      <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', color }}>
        {label}
      </Typography>
    );
    return [
      // { groupId: 'anticipated_model', headerName: 'Anticipated Model', headerClassName: 'group-divider-left', children: [{ field: 'total_ant_cost_no_credit' }, { field: 'avg_acuity_gap' }, { field: 'pct_ant_days_any_below' }], renderHeaderGroup: renderGroup('Anticipated Model', 'rgba(21, 101, 192, 0.85)') },
      { groupId: 'hybrid_model', headerName: 'Hybrid Model', headerClassName: 'group-divider-left', children: [{ field: 'hybrid_mds_cost' }, { field: 'hybrid_pct_days_any_below' }], renderHeaderGroup: renderGroup('Hybrid Model', 'rgba(123, 31, 162, 0.85)') },
      { groupId: 'state_minimum', headerName: 'State Minimum', headerClassName: 'group-divider-left', children: [{ field: 'pct_days_below_cms_hprd' }], renderHeaderGroup: renderGroup('State Minimum', 'rgba(46, 125, 50, 0.85)') },
      { groupId: 'nav_group', headerName: '', headerClassName: 'group-divider-left', children: [{ field: '__navigate' }], renderHeaderGroup: () => null },
    ];
  })();

  // ---- Render ----

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const metricLabelSx = { fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 } as const;
  const metricBoxSx = { flexShrink: 0, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' } as const;

  return (
    <Layout>
      {/* Dark header — matching state page */}
      <Box
        mb={4}
        sx={{
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Box
          p={3}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            transition: 'background-color 0.2s',
          }}
          onClick={() => setHeaderExpanded(!headerExpanded)}
        >
          <Box flex={1}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Skilled Nursing Facilities: Understaffing
            </Typography>
          </Box>
          <IconButton size="small" sx={{ color: 'inherit', ml: 2 }}>
            {headerExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={headerExpanded} timeout="auto">
          <Box px={3} pb={3}>
            {/* Two-column layout: Understaffing left, Models right */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2.5 }}>
              {/* Left: Understaffing + Bellwether Cases */}
              <Box sx={{ flex: 1.2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, mt: 1, fontSize: '0.95rem' }}>Understaffing</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                  Nursing homes that accept Medicare and Medicaid payments agree to staff their facilities to meet each resident's assessed clinical needs. When a facility systematically provides fewer nursing hours than its residents require—while continuing to bill the government for full reimbursement—it is unlawfully collecting money it is not entitled to, sometimes rising to the level of an actionable fraud case.
                  {'\n\n'}
                  EliotNest identifies understaffing by comparing a facility's actual staffing levels (from CMS Payroll-Based Journal data) against the staffing levels that its residents required (from MDS patient assessments), for every facility in the country, every day, from 2020 to the present.
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 700, mt: 2.5, mb: 0.5, fontSize: '0.95rem' }}>Bellwether Cases</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.6, display: 'block', mb: 1 }}>
                  <strong><em>State of New Jersey v. Deptford Center and Hammonton Center</em> (January 2026)</strong><br />New Jersey sued the operators of two nursing homes for intentional understaffing and diversion of Medicaid funds through nine related-party entities. The suit names 34 defendants and seeks <strong>$124 million</strong> in repayment.
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.6, display: 'block' }}>
                  <strong><em>People of the State of California v. Sweetwater Care et al.</em> (June 2025)</strong><br />California sued a chain of 17 nursing homes for over 25,000 staffing-law violations between 2020 and 2024, alleging that operators extracted $31 million in profits rather than investing in required staffing. Potential penalties: up to <strong>$2,500 per violation</strong> under Bus. &amp; Prof. Code § 17200 plus <strong>$100,000 per day</strong> of staffing shortfalls under H&amp;SC § 1276.65—total exposure potentially exceeding $62 million on the per-violation count alone.
                </Typography>
              </Box>

              {/* Right: Models */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, borderLeft: '1px solid rgba(255,255,255,0.15)', pl: 3 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'rgba(144, 202, 249, 1)', mb: 0.25, mt: 1, fontSize: '0.95rem' }}>Anticipated Model</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block' }}>
                    The Anticipated Model uses the CMS Staff Time Measurement study—the government's own empirical benchmark of how many RN, LPN, and CNA minutes each type of resident requires—to calculate expected staffing for each facility based on its actual resident population. The gap between expected and actual hours is the understaffing deficit. We calculate the dollar amount by using each facility's own average salary for its specific staffing group.
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block', mt: 1.5 }}>
                    <strong>With Staffing Credit</strong> allows overstaffing in one nursing category (e.g., excess LPN hours) to offset shortfalls in another (e.g., insufficient RN hours). This gives facilities some credit for—most commonly—overstaffing LPNs to account for nursing shortages, despite those roles providing completely different types of care.
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block', mt: 1.5 }}>
                    <strong>Without Staffing Credit</strong> counts only the categories where the facility fell short, with no offset. This reflects the position that each nursing category serves a distinct clinical function—a surplus of aides cannot substitute for a shortage of registered nurses.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'rgba(206, 147, 216, 1)', mb: 0.25, fontSize: '0.95rem' }}>Hybrid Model</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block' }}>
                    The Hybrid Model applies the same methodology as the Anticipated Model but uses a more demanding benchmark. Instead of relying solely on the CMS Staff Time Measurement study, it incorporates upward staffing adjustments recommended by Harrington et al., "Appropriate Nurse Staffing Levels for U.S. Nursing Homes," <em>Health Services Insights</em>, Vol. 13 (2020).
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', mb: 0.25, fontSize: '0.95rem' }}>Percentage of Days</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block' }}>
                    The Percent Days Model asks a binary question for each day: did the facility meet the applicable staffing benchmark, or did it not? Rather than calculating a continuous gap, it reports the percentage of days during the analysis period on which the facility was understaffed.
                    {' '}This is a straightforward metric for enforcement: the facility was understaffed on X% of days over Y years. On those days, regulators can seek to recover Medicaid payments under a breach of contract theory.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'rgba(165, 214, 167, 1)', mb: 0.25, fontSize: '0.95rem' }}>State Minimum</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block' }}>
                    In states with numeric Hours Per Resident Day (HPRD) requirements, this model asks whether the facility met the state's minimum staffing standard on a given day.
                    {' '}This model is limited to those states that impose quantitative minimums, but where applicable it provides a straightforward basis for liability because the standard is an explicit number set by statute or regulation.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* National Summary Banner — matching state page structure */}
      <Paper elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, minHeight: 110 }}>
      {nationalSummary ? (
        <>
          <Typography fontWeight={800} fontSize="1.4rem" color="text.primary" sx={{ mb: 1.5 }}>
            United States — National Overview
          </Typography>

          <Divider sx={{ mb: 1.5 }} />

          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2, justifyContent: 'center' }}>
            <Box sx={{ flex: 1, ...metricBoxSx }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>Total Facilities</Typography>
              <Typography fontWeight={700} fontSize="1.25rem">{formatNumber(nationalSummary.num_facilities)}</Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Tooltip title="Average hours per resident day across all facilities nationally, based on PBJ staffing data." arrow>
              <Box sx={{ flex: 1, ...metricBoxSx, cursor: 'help' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>Avg HPRD</Typography>
                <Typography fontWeight={700} fontSize="1.25rem">{formatNumber(nationalSummary.avg_hprd, 2)}</Typography>
              </Box>
            </Tooltip>

            {/* Projected Understaffing Cost card removed
            <Divider orientation="vertical" flexItem />

            <Tooltip title="Estimated national labor cost of understaffing vs MDS acuity-based requirements using the Hybrid model, with overstaffing credit (per-year floored at $0)." arrow>
              <Box sx={{ flex: 1, ...metricBoxSx, cursor: 'help' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>Projected Understaffing Cost</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>(Hybrid, With Credit)</Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block' }}>2020-2025</Typography>
                <Typography fontWeight={700} fontSize="1.1rem" sx={{ mt: 0.5 }}>{formatMoney(nationalSummary.hybrid_mds_cost)}</Typography>
              </Box>
            </Tooltip>
            */}

            <Divider orientation="vertical" flexItem />

            <Tooltip title="Average percentage of days facilities fail to staff to MDS acuity requirements (Hybrid model)." arrow>
              <Box sx={{ flex: 1, ...metricBoxSx, cursor: 'help' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>% Days Fail to Staff to Acuity</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>(Hybrid)</Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block' }}>2020-2025</Typography>
                <Typography fontWeight={700} fontSize="1.1rem" sx={{ mt: 0.5 }}>{nationalSummary.hybrid_pct_days_any_below != null ? formatPercent(nationalSummary.hybrid_pct_days_any_below) : '—'}</Typography>
              </Box>
            </Tooltip>

            <Divider orientation="vertical" flexItem />

            <Tooltip title="Average percentage of days facilities fall below the applicable state or CMS minimum staffing threshold." arrow>
              <Box sx={{ flex: 1, ...metricBoxSx, cursor: 'help' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>% Days Fail to Staff to State Minimum</Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block' }}>2020-2025</Typography>
                <Typography fontWeight={700} fontSize="1.1rem" sx={{ mt: 0.5 }}>{nationalSummary.pct_days_below_cms_hprd != null ? formatPercent(nationalSummary.pct_days_below_cms_hprd) : '—'}</Typography>
              </Box>
            </Tooltip>
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      </Paper>

      {/* Search and Filters Bar — matching state page structure */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          pb: 2,
          mb: 4,
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          overflow: 'visible',
        }}
      >
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder={activeView === 'states' ? 'Search states...' : 'Search ownership groups...'}
            value={activeView === 'states' ? stateSearch : ownerGroupsSearch}
            onChange={(e) => {
              if (activeView === 'states') {
                setStateSearch(e.target.value);
              } else {
                setOwnerGroupsSearch(e.target.value);
                setOwnerGroupsPagination(prev => ({ ...prev, page: 0 }));
              }
            }}
            sx={{
              minWidth: 280,
              backgroundColor: 'background.default',
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': { borderColor: 'primary.main' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          {activeView === 'ownerGroups' && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>State</InputLabel>
              <Select
                value={ownerGroupsStateFilter}
                label="State"
                onChange={(e) => {
                  setOwnerGroupsStateFilter(e.target.value);
                  setOwnerGroupsPagination(prev => ({ ...prev, page: 0 }));
                }}
                sx={{ backgroundColor: 'background.default' }}
              >
                <MenuItem value=""><em>All States</em></MenuItem>
                {Object.entries(STATE_NAMES).sort(([,a], [,b]) => a.localeCompare(b)).map(([code, name]) => (
                  <MenuItem key={code} value={code}>{code} — {name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box flex={1} />
          <ToggleButtonGroup
            value={activeView}
            exclusive
            onChange={(_e, val) => {
              if (val) {
                setActiveView(val);
                setOwnerGroupsPagination(prev => ({ ...prev, page: 0 }));
              }
            }}
            size="small"
          >
            <ToggleButton value="states" sx={{
              '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
            }}>States</ToggleButton>
            <ToggleButton value="ownerGroups" sx={{
              '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
            }}>Ownership Groups</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 0.5, pt: 0.5, display: 'block', mt: -0.5 }}>
          * Based on CMS PBJ staffing and MDS acuity data from 2020-2025
        </Typography>

        {/* Data Table */}
        <Box sx={{ minHeight: 800, height: '85vh' }}>
        {activeView === 'states' ? (
          <DataGrid
            key="states-grid"
            rows={processedStateRows}
            columns={stateColumns}
            getRowHeight={() => 'auto'}
            loading={statesLoading}
            getRowId={(row) => row.state}
            sortingMode="server"
            sortModel={stateSortModel}
            onSortModelChange={setStateSortModel}
            columnGroupingModel={stateColumnGroups}
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            getRowClassName={(params) => params.row._isDetailRow ? 'detail-panel-row' : ''}
            onRowClick={(params) => {
              if (params.row._isDetailRow) return;
              const st = params.row.state;
              setExpandedStateRows(prev => {
                const next = new Set(prev);
                if (next.has(st)) next.delete(st);
                else next.add(st);
                return next;
              });
            }}
            sx={dataGridStyles}
          />
        ) : (
          <DataGrid
            rows={processedOwnerGroupRows}
            columns={ownerGroupColumns}
            columnGroupingModel={ownerGroupColumnGroups}
            getRowHeight={() => 'auto'}
            loading={ownerGroupsLoading}
            getRowId={(row) => row.group_id}
            paginationMode="server"
            paginationModel={ownerGroupsPagination}
            onPaginationModelChange={setOwnerGroupsPagination}
            rowCount={ownerGroupsRowCount}
            pageSizeOptions={[10, 25, 50]}
            sortingMode="server"
            sortModel={ownerGroupsSortModel}
            onSortModelChange={setOwnerGroupsSortModel}
            disableColumnMenu
            disableRowSelectionOnClick
            getRowClassName={(params) => params.row._isDetailRow ? 'detail-panel-row' : ''}
            onRowClick={(params) => {
              if (params.row._isDetailRow) return;
              // Toggle expand on row click
              const rowId = params.row.group_id;
              setExpandedRows(prev => {
                const next = new Set(prev);
                if (next.has(rowId)) next.delete(rowId);
                else next.add(rowId);
                return next;
              });
            }}
            sx={dataGridStyles}
          />
        )}
        </Box>
      </Paper>
      {/* ───── Section: Data Sources ───── */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Data Sources
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Dataset</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>View</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Minimum Data Set (MDS)</TableCell>
                  <TableCell>Daily census of anonymized patient acuity assessments for every nursing facility in the US. Used to calculate expected staffing levels based on actual patient care needs.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://www.cms.gov/medicare/quality/nursing-home-improvement/minimum-data-sets-swing-bed-providers" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Payroll Based Journal Daily Nurse Staffing</TableCell>
                  <TableCell>Provides daily hours for each staff type (nursing) per facility.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/quality-of-care/payroll-based-journal-daily-nurse-staffing" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Payroll Based Journal Daily Non-Nurse Staffing</TableCell>
                  <TableCell>Provides daily hours for each staff type (non-nursing) per facility.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/quality-of-care/payroll-based-journal-daily-non-nurse-staffing" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Skilled Nursing Facility Enrollments</TableCell>
                  <TableCell>Information on Skilled Nursing Facilities currently enrolled in Medicare. Provides names, addresses, and other related data for each SNF.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/provider-characteristics/hospitals-and-other-facilities/skilled-nursing-facility-enrollments" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Skilled Nursing Facility All Owners</TableCell>
                  <TableCell>Provides information on all owners of SNFs currently enrolled in Medicare.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/provider-characteristics/hospitals-and-other-facilities/skilled-nursing-facility-all-owners" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>SNF Ownership Change</TableCell>
                  <TableCell>List of every ownership change for all SNFs.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/provider-characteristics/hospitals-and-other-facilities/skilled-nursing-facility-change-of-ownership" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>SNF Cost Reports (Form 2540-10)</TableCell>
                  <TableCell>Skilled nursing facility cost reports from HCRIS, providing financial data for Medicare reimbursement analysis.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://www.cms.gov/data-research/statistics-trends-and-reports/cost-reports/skilled-nursing-facility-2540-2010-form" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

    </Layout>
  );
}
